// src/hooks/useHasFlagPermission.ts
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../redux/slices/auth.slice';

/**
 * Checks whether the current user may use a UI flag control (block toggle on Users/Roles tables).
 *
 * AUDIT NOTE (intentional — do not treat as a client-facing security gap):
 * This hook is only used for the block/unblock switch in Settings → Users Management and
 * Settings → Roles Management. Those menus are provider-only: client store roles (cashier,
 * branch manager, etc.) must never be granted `settings.users-management.view` or
 * `settings.roles-management.view`. Only the platform provider team uses these screens.
 *
 * Access is enforced server-side on every API call (`@RequirePermission` on view). A client
 * user cannot reach or mutate users/roles without that permission, even if they know the URL.
 *
 * Inventory KPI column flags are a separate mechanism and use `usePermission()` with
 * `resource: 'flag'` — not this hook.
 *
 * Returning `true` for any authenticated user on these pages is acceptable while only
 * provider accounts hold the parent menu permissions. If client roles ever receive users/roles
 * management access, replace this with `usePermission(SETTINGS_PERMISSIONS.*.BLOCK)` and
 * enforce the block permission on `PUT .../block` endpoints as well.
 *
 * LP
 *
 * @param module - Module id (e.g. 'settings')
 * @param menu - Menu id (e.g. 'roles-management')
 * @param flag - Flag id (e.g. 'block')
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useHasFlagPermission = (module: string, menu: string, flag: string): boolean => {
  const user = useSelector(selectCurrentUser);

  if (user) {
    return true;
  }

  return false;
};
